import { frameDisplayUrl } from "../utils/assetMatching";
import { useRef } from "react";
import type { StoryboardFrame } from "../types";
import { frameSourceClass, frameSourceLabel, frameStatus } from "../utils/storyboard";
import { Icon } from "./Icon";

interface FrameEditorProps {
  frame: StoryboardFrame;
  onToggleLock: () => void;
  onToggleQueue: () => void;
  onToggleDisable: () => void;
  onClear: () => void;
  onUpload: (file: File) => void;
  onOpenPrompt: () => void;
}

export function FrameEditor({ frame, onToggleLock, onToggleQueue, onToggleDisable, onClear, onUpload, onOpenPrompt }: FrameEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isGenerating = frame.generationStatus === "generating" || frame.generationStatus === "queued";
  const queueLabel = frame.generationStatus === "failed" ? "Queue Retry" : frame.queuedRegenerate ? "Unqueue" : "Queue Regenerate";
  const uploadLabel = frame.generationStatus === "failed" || frame.generationStatus === "prompt_ready" || isGenerating
    ? "Upload instead"
    : "Replace";

  return (
    <section className="frame-editor" aria-label="Selected board editor">
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept="image/*"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(file);
          event.target.value = "";
        }}
      />
      <div className="editor-preview">
        <img src={frameDisplayUrl(frame)} alt={frame.label} />
        <div className="editor-preview-badges">
          <span>{frameStatus(frame)}</span>
          <span className={`editor-source ${frameSourceClass(frame)}`}>{frameSourceLabel(frame)}</span>
        </div>
      </div>
      <div className="editor-copy">
        <span>Selected Board</span>
        <strong>{frame.shotType}</strong>
        <p>{frame.prompt || "Tune this board before the EvoLink review package."}</p>
        {frame.generationError ? <p className="editor-warning">{frame.generationError}</p> : null}
      </div>
      <div className="editor-actions">
        <button className="secondary-action" onClick={() => inputRef.current?.click()} type="button">
          <Icon name="upload" />
          {uploadLabel}
        </button>
        <button className="secondary-action" onClick={onToggleLock} type="button">
          <Icon name="lock" />
          {frame.locked ? "Unlock" : "Lock"}
        </button>
        <button className="secondary-action" onClick={onOpenPrompt} type="button">
          <Icon name="script" />
          Prompt
        </button>
        <button className="secondary-action" disabled={frame.locked || frame.disabled || isGenerating} onClick={onToggleQueue} type="button">
          {queueLabel}
        </button>
        <button className="secondary-action" onClick={onToggleDisable} type="button">
          {frame.disabled ? "Enable" : "Disable"}
        </button>
        <button className="secondary-action" onClick={onClear} type="button">Clear</button>
      </div>
    </section>
  );
}
